/**
 * Character Animator (hackathon).
 *
 * Three inputs, three outputs:
 *   1. Describe a character  → a portrait from Nano Banana 2 lite, chroma-keyed
 *      to a transparent background (see chromaKey.ts on the service side).
 *   2. Describe what it does → a 6×4 sprite sheet from Nano Banana 2, using the
 *      portrait as a reference, also keyed to transparency, sliced into a
 *      looping animation on screen.
 *   3. Duration + the same action description → a short sound effect from
 *      ElevenLabs. Independent of the two image calls.
 *
 * Step 2 is gated on step 1: the animation needs the character image as its
 * reference, so the "Animate" button stays disabled until a portrait exists.
 * Step 3 has no such gate — it needs only the action text, not any image.
 *
 * The prompt template for each of the three calls is shown in an editable text
 * box, seeded from GET /animator/prompts and sent back with each generate call.
 * There's no persistence beyond the page: editing a box changes what's sent for
 * the rest of this session, same as the sfx-generator tool this was ported from.
 *
 * State lives here rather than in a store — the page has no session and nothing
 * to persist, so a handful of useState hooks are the whole model.
 */

import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Image,
  StyleSheet,
  Platform,
} from "react-native";
import { colors, textStyle, backButtonStyle } from "../styles";
import { useNavigation } from "../navigation/useNavigation";
import {
  getPromptTemplates,
  generateCharacter,
  generateAnimation,
  generateSoundEffect,
  type AnimationResult,
  type CharacterResult,
} from "./animatorApi";
import SpriteSheetPlayer from "./SpriteSheetPlayer";

/** Sound duration slider bounds — matches the backend's accepted range. 0 = auto. */
const MIN_DURATION = 0;
const MAX_DURATION = 22;
const DEFAULT_DURATION = 2;

export default function AnimatorPage() {
  const { goBack } = useNavigation();

  // --- prompt templates (editable) ---
  const [charTemplate, setCharTemplate] = useState("");
  const [animTemplate, setAnimTemplate] = useState("");
  const [soundTemplate, setSoundTemplate] = useState("");
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  useEffect(() => {
    getPromptTemplates()
      .then((t) => {
        setCharTemplate(t.character);
        setAnimTemplate(t.animation);
        setSoundTemplate(t.sound);
      })
      .catch(() => {
        // Templates are a nice-to-have editing surface; if the fetch fails the
        // page still works — the backend falls back to its own defaults when
        // promptTemplate is omitted.
      })
      .finally(() => setTemplatesLoaded(true));
  }, []);

  // --- descriptions ---
  const [charDesc, setCharDesc] = useState("");
  const [actionDesc, setActionDesc] = useState("");
  const [duration, setDuration] = useState(DEFAULT_DURATION);

  // --- results ---
  const [charResult, setCharResult] = useState<CharacterResult | null>(null);
  const [animation, setAnimation] = useState<AnimationResult | null>(null);
  const [soundBase64, setSoundBase64] = useState<string | null>(null);

  // --- loading / error ---
  const [charLoading, setCharLoading] = useState(false);
  const [animLoading, setAnimLoading] = useState(false);
  const [soundLoading, setSoundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- hover-to-reveal raw (pre-key / pre-slice) images ---
  const [showRawChar, setShowRawChar] = useState(false);
  const [showRawAnim, setShowRawAnim] = useState(false);

  const onGenerateCharacter = async () => {
    if (!charDesc.trim() || charLoading) return;
    setError(null);
    setCharLoading(true);
    // A new character invalidates any animation built from the old one.
    setAnimation(null);
    try {
      const result = await generateCharacter(charDesc.trim(), charTemplate || undefined);
      setCharResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not make the character.");
    } finally {
      setCharLoading(false);
    }
  };

  const onGenerateAnimation = async () => {
    if (!actionDesc.trim() || !charResult || animLoading) return;
    setError(null);
    setAnimLoading(true);
    try {
      const result = await generateAnimation(
        actionDesc.trim(),
        charResult.imageBase64,
        animTemplate || undefined,
      );
      setAnimation(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not make the animation.");
    } finally {
      setAnimLoading(false);
    }
  };

  const onGenerateSound = async () => {
    if (!actionDesc.trim() || !charDesc.trim() || soundLoading) return;
    setError(null);
    setSoundLoading(true);
    try {
      const result = await generateSoundEffect(
        actionDesc.trim(),
        charDesc.trim(),
        duration,
        soundTemplate || undefined,
      );
      setSoundBase64(result.audioBase64);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not make the sound.");
    } finally {
      setSoundLoading(false);
    }
  };

  const canAnimate = !!charResult && !!actionDesc.trim() && !animLoading;
  // Needs the character TEXT (what is making the sound), not the generated
  // image — unlike step 2, this call has no reference image, so the
  // description itself has to travel in the prompt every time.
  const canMakeSound = !!actionDesc.trim() && !!charDesc.trim() && !soundLoading;

  return (
    <View style={styles.screen}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={goBack} style={backButtonStyle.container} testID="animator-back">
          <Text style={styles.backIcon}>{"‹"}</Text>
        </Pressable>
        <Text style={styles.title}>Character Animator</Text>
        <View style={{ width: 48 }} />
      </View>

      <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
        {error ? (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : null}

        <View style={styles.columns}>
          {/* ---- Step 1: character ---- */}
          <View style={styles.card}>
            <Text style={styles.step}>1 · Describe your character</Text>
            <TextInput
              style={styles.input}
              value={charDesc}
              onChangeText={setCharDesc}
              placeholder="A friendly orange dragon with tiny wings and big eyes"
              placeholderTextColor={colors.textDim}
              multiline
              editable={!charLoading}
              testID="animator-character-input"
            />
            <PromptTemplateBox
              label="Character prompt template"
              value={charTemplate}
              onChangeText={setCharTemplate}
              loaded={templatesLoaded}
              testID="animator-character-template"
            />
            <Pressable
              style={[styles.btn, (!charDesc.trim() || charLoading) && styles.btnDisabled]}
              disabled={!charDesc.trim() || charLoading}
              onPress={onGenerateCharacter}
              testID="animator-character-generate"
            >
              <Text style={styles.btnText}>
                {charLoading ? "Drawing…" : charResult ? "Redraw character" : "Make character"}
              </Text>
            </Pressable>

            <View style={styles.preview}>
              {charLoading ? (
                <ActivityIndicator color={colors.accent} />
              ) : charResult ? (
                <Pressable
                  onHoverIn={() => setShowRawChar(true)}
                  onHoverOut={() => setShowRawChar(false)}
                  onPressIn={() => setShowRawChar(true)}
                  onPressOut={() => setShowRawChar(false)}
                  style={styles.hoverArea}
                  testID="animator-character-preview"
                >
                  <Image
                    source={{
                      uri: `data:image/png;base64,${
                        showRawChar ? charResult.rawImageBase64 : charResult.imageBase64
                      }`,
                    }}
                    style={styles.charImage}
                    resizeMode="contain"
                  />
                  <Text style={styles.hoverHint}>
                    {showRawChar ? "Raw model output (before removing background)" : "Hold to see raw output"}
                  </Text>
                </Pressable>
              ) : (
                <Text style={styles.previewHint}>Your character shows up here.</Text>
              )}
            </View>
          </View>

          {/* ---- Step 2: animation ---- */}
          <View style={[styles.card, !charResult && styles.cardLocked]}>
            <Text style={styles.step}>2 · What should it do?</Text>
            <TextInput
              style={styles.input}
              value={actionDesc}
              onChangeText={setActionDesc}
              placeholder="Jumping up and down happily"
              placeholderTextColor={colors.textDim}
              multiline
              editable={!!charResult && !animLoading}
              testID="animator-action-input"
            />
            <PromptTemplateBox
              label="Animation prompt template"
              value={animTemplate}
              onChangeText={setAnimTemplate}
              loaded={templatesLoaded}
              testID="animator-animation-template"
            />
            <Pressable
              style={[styles.btn, !canAnimate && styles.btnDisabled]}
              disabled={!canAnimate}
              onPress={onGenerateAnimation}
              testID="animator-animation-generate"
            >
              <Text style={styles.btnText}>{animLoading ? "Animating…" : "Animate!"}</Text>
            </Pressable>
            {!charResult ? (
              <Text style={styles.lockHint}>Make a character first.</Text>
            ) : null}

            <View style={styles.preview}>
              {animLoading ? (
                <ActivityIndicator color={colors.accent} />
              ) : animation ? (
                <Pressable
                  onHoverIn={() => setShowRawAnim(true)}
                  onHoverOut={() => setShowRawAnim(false)}
                  onPressIn={() => setShowRawAnim(true)}
                  onPressOut={() => setShowRawAnim(false)}
                  style={styles.hoverArea}
                  testID="animator-animation-preview"
                >
                  {showRawAnim ? (
                    <Image
                      source={{ uri: `data:image/png;base64,${animation.rawImageBase64}` }}
                      style={styles.rawSheetImage}
                      resizeMode="contain"
                    />
                  ) : (
                    <SpriteSheetPlayer
                      imageBase64={animation.imageBase64}
                      cols={animation.cols}
                      rows={animation.rows}
                      size={Platform.OS === "web" ? 320 : 240}
                    />
                  )}
                  <Text style={styles.hoverHint}>
                    {showRawAnim ? "Raw sheet, before removing background" : "Hold to see the raw sheet"}
                  </Text>
                </Pressable>
              ) : (
                <Text style={styles.previewHint}>Your looping animation plays here.</Text>
              )}
            </View>
          </View>

          {/* ---- Step 3: sound ---- */}
          <View style={styles.card}>
            <Text style={styles.step}>3 · Give it a sound</Text>
            <Text style={styles.subHint}>Uses the character from step 1 and the action from step 2, above.</Text>

            <View style={styles.durationRow}>
              <Text style={styles.durationLabel}>
                Duration: {duration === 0 ? "auto" : `${duration.toFixed(1)}s`}
              </Text>
              <View style={styles.durationButtons}>
                <Pressable
                  style={styles.durationBtn}
                  onPress={() => setDuration((d) => Math.max(MIN_DURATION, Math.round((d - 0.5) * 10) / 10))}
                  testID="animator-duration-minus"
                >
                  <Text style={styles.durationBtnText}>−</Text>
                </Pressable>
                <Pressable
                  style={styles.durationBtn}
                  onPress={() => setDuration((d) => Math.min(MAX_DURATION, Math.round((d + 0.5) * 10) / 10))}
                  testID="animator-duration-plus"
                >
                  <Text style={styles.durationBtnText}>+</Text>
                </Pressable>
              </View>
            </View>

            <PromptTemplateBox
              label="Sound prompt template"
              value={soundTemplate}
              onChangeText={setSoundTemplate}
              loaded={templatesLoaded}
              testID="animator-sound-template"
            />
            <Pressable
              style={[styles.btn, !canMakeSound && styles.btnDisabled]}
              disabled={!canMakeSound}
              onPress={onGenerateSound}
              testID="animator-sound-generate"
            >
              <Text style={styles.btnText}>{soundLoading ? "Listening…" : "Make sound"}</Text>
            </Pressable>
            {!charDesc.trim() ? (
              <Text style={styles.lockHint}>Describe a character in step 1 first.</Text>
            ) : !actionDesc.trim() ? (
              <Text style={styles.lockHint}>Describe an action in step 2 first.</Text>
            ) : null}

            <View style={styles.preview}>
              {soundLoading ? (
                <ActivityIndicator color={colors.accent} />
              ) : soundBase64 ? (
                Platform.OS === "web" ? (
                  <audio controls src={`data:audio/mpeg;base64,${soundBase64}`} style={{ width: "100%" }} />
                ) : (
                  <Text style={styles.previewHint}>Sound made — playback is web-only in this prototype.</Text>
                )
              ) : (
                <Text style={styles.previewHint}>Your sound effect plays here.</Text>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

/** An editable prompt template box, seeded from the server default. */
function PromptTemplateBox({
  label,
  value,
  onChangeText,
  loaded,
  testID,
}: {
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  loaded: boolean;
  testID: string;
}) {
  return (
    <View style={styles.templateBlock}>
      <Text style={styles.templateLabel}>{label}</Text>
      <TextInput
        style={styles.templateInput}
        value={loaded ? value : "Loading…"}
        onChangeText={onChangeText}
        editable={loaded}
        multiline
        testID={testID}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  backIcon: { color: colors.white, fontSize: 28, lineHeight: 30, marginTop: -2 },
  title: { color: colors.text, fontSize: 24, ...textStyle.heading },
  body: { padding: 16, paddingBottom: 48 },
  columns: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "center",
  },
  card: {
    flexGrow: 1,
    flexBasis: 340,
    maxWidth: 460,
    backgroundColor: colors.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 18,
    gap: 12,
  },
  cardLocked: { opacity: 0.6 },
  step: { color: colors.textSecondary, fontSize: 16, ...textStyle.bodyBold },
  subHint: { color: colors.textMuted, fontSize: 13, ...textStyle.body, marginTop: -8 },
  input: {
    minHeight: 72,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 12,
    color: colors.text,
    backgroundColor: colors.white,
    ...textStyle.body,
    fontSize: 15,
    textAlignVertical: "top",
  },
  templateBlock: { gap: 4 },
  templateLabel: { color: colors.textMuted, fontSize: 12, ...textStyle.bodyMedium },
  templateInput: {
    minHeight: 300,
    borderWidth: 1,
    borderColor: colors.borderLight,
    borderRadius: 8,
    padding: 10,
    color: colors.textSecondary,
    backgroundColor: colors.surfaceHover,
    fontSize: 12,
    fontFamily: "ui-monospace, monospace",
    textAlignVertical: "top",
  },
  durationRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  durationLabel: { color: colors.text, fontSize: 14, ...textStyle.bodyMedium },
  durationButtons: { flexDirection: "row", gap: 8 },
  durationBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.surfaceHover,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: "center",
    justifyContent: "center",
  },
  durationBtnText: { color: colors.text, fontSize: 18, ...textStyle.bodyBold },
  btn: {
    backgroundColor: colors.accent,
    borderRadius: 24,
    paddingVertical: 12,
    alignItems: "center",
  },
  btnDisabled: { backgroundColor: colors.textDim },
  btnText: { color: colors.white, fontSize: 16, ...textStyle.heading },
  lockHint: { color: colors.textMuted, fontSize: 13, ...textStyle.body, textAlign: "center" },
  preview: {
    minHeight: 260,
    borderRadius: 12,
    backgroundColor: colors.surfaceDark,
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    gap: 8,
  },
  hoverArea: { alignItems: "center", justifyContent: "center", gap: 8, width: "100%" },
  hoverHint: { color: colors.textDim, fontSize: 11, ...textStyle.body, textAlign: "center" },
  charImage: { width: "100%", height: 280, maxWidth: 280, borderRadius: 12 },
  rawSheetImage: { width: "100%", height: 280, maxWidth: 320, borderRadius: 8 },
  previewHint: { color: colors.textMuted, ...textStyle.body, textAlign: "center" },
  errorBox: {
    backgroundColor: "#FBEBEC",
    borderColor: colors.danger,
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  },
  errorText: { color: colors.danger, ...textStyle.bodyMedium, textAlign: "center" },
});
